import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { favoritesService } from '../services';
import { transformListing, updateListingWithPropertyData } from '../components/listings/utils/dataTransformers';

// Create a context for favorites
const FavoritesContext = createContext();

// Custom hook to use the favorites context
export const useFavorites = () => useContext(FavoritesContext);

// Transform favorites data to match expected format
const transformFavoriteData = (favorite) => {
  // First apply standard listing transformation
  let transformed = transformListing(favorite);
  
  // Handle the favorited_at field (database returns created_at)
  if (favorite.created_at) {
    transformed.favorited_at = favorite.created_at;
  }
  
  // If we have market value data, apply those transformations too
  if (favorite.marketValue || favorite.estimated_value) {
    const marketValue = favorite.marketValue || favorite.estimated_value;
    const propertyData = {
      status: 'success',
      data: {
        estimatedValue: marketValue,
        source: favorite.dataSource || 'Database'
      }
    };
    transformed = updateListingWithPropertyData(transformed, propertyData);
  }
  
  return transformed;
};

// Provider component for favorites
export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // Load favorites from database when user changes
  useEffect(() => {
    const loadFavorites = async () => {
      if (!currentUser) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      try {
        const favoritesData = await favoritesService.getFavorites();
        // apiClient returns the response directly, not wrapped in data
        const transformedFavorites = Array.isArray(favoritesData) 
          ? favoritesData.map(transformFavoriteData) 
          : [];
        setFavorites(transformedFavorites);
        // Removed debug logging
      } catch (error) {
        console.error('Failed to load favorites:', error);
        setFavorites([]); // Ensure favorites is always an array on error
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [currentUser]);

  // Add a property to favorites
  const addFavorite = async (property) => {
    if (!currentUser) return;

    const propertyId = property.id || property.property_id;
    
    // Check if already favorited to avoid unnecessary API calls
    if (isFavorite(propertyId)) {
      // Removed debug logging
      return;
    }

    try {
      const response = await favoritesService.addFavorite(propertyId);
      // Removed debug logging
      
      // Update local state immediately for better UX
      setFavorites(prev => {
        const currentFavorites = Array.isArray(prev) ? prev : [];
        const exists = currentFavorites.some(fav => 
          fav.id === property.id || fav.property_id === property.property_id
        );
        if (exists) return currentFavorites;
        
        const propertyWithTimestamp = transformFavoriteData({
          ...property,
          created_at: new Date().toISOString(),
          notes: ''
        });
        
        return [...currentFavorites, propertyWithTimestamp];
      });
    } catch (error) {
      // Handle 409 Conflict (already favorited) gracefully
      if (error.status === 409) {
        // Removed debug logging
        // Update local state to reflect the favorite status
        setFavorites(prev => {
          const currentFavorites = Array.isArray(prev) ? prev : [];
          const exists = currentFavorites.some(fav => 
            fav.id === property.id || fav.property_id === property.property_id
          );
          if (!exists) {
            const propertyWithTimestamp = transformFavoriteData({
              ...property,
              created_at: new Date().toISOString(),
              notes: ''
            });
            return [...currentFavorites, propertyWithTimestamp];
          }
          return currentFavorites;
        });
        return; // Don't show error for duplicate favorites
      }
      console.error('Failed to add favorite:', error);
    }
  };

  // Remove a property from favorites
  const removeFavorite = async (propertyId) => {
    if (!currentUser) return;

    try {
      await favoritesService.removeFavorite(propertyId);
      setFavorites(prev => {
        const currentFavorites = Array.isArray(prev) ? prev : [];
        return currentFavorites.filter(fav => 
          fav.id !== propertyId && fav.property_id !== propertyId
        );
      });
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  // Update notes for a favorited property
  const updateFavoriteNotes = async (propertyId, notes) => {
    if (!currentUser) return;

    try {
      await favoritesService.updateNotes(propertyId, notes);
      setFavorites(prev => {
        const currentFavorites = Array.isArray(prev) ? prev : [];
        return currentFavorites.map(fav => {
          if (fav.id === propertyId || fav.property_id === propertyId) {
            return { ...fav, notes };
          }
          return fav;
        });
      });
    } catch (error) {
      console.error('Failed to update notes:', error);
    }
  };

  // Check if a property is in favorites
  const isFavorite = (propertyId) => {
    if (!Array.isArray(favorites) || !propertyId) {
      return false;
    }
    return favorites.some(fav => 
      fav.id === propertyId || fav.property_id === propertyId
    );
  };

  // Clear all favorites
  const clearFavorites = async () => {
    if (!currentUser) return;

    try {
      await favoritesService.clearFavorites();
      setFavorites([]);
    } catch (error) {
      console.error('Failed to clear favorites:', error);
    }
  };

  // Refresh favorites from server
  const refreshFavorites = async () => {
    if (!currentUser) return;

    try {
      const favoritesData = await favoritesService.getFavorites();
      const transformedFavorites = Array.isArray(favoritesData) 
        ? favoritesData.map(transformFavoriteData) 
        : [];
      setFavorites(transformedFavorites);
      // Removed debug logging
    } catch (error) {
      console.error('Failed to refresh favorites:', error);
    }
  };

  // The value provided to consumers of this context
  const value = {
    favorites,
    addFavorite,
    removeFavorite,
    updateFavoriteNotes,
    isFavorite,
    clearFavorites,
    refreshFavorites,
    loading
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesContext; 